// Self-service profile editing. Anyone signed in can edit their own.
// Renders inside the AppShell so the page sits alongside Team rather
// than the auth-style centred card.
//
// Why local form state: Firestore is the source of truth via the
// AuthContext snapshot, but typing into a controlled input that's reset
// on every snapshot would feel awful. We seed local state from profile
// on mount + after successful save, and let the user edit freely between.

import { useEffect, useState } from 'react';
import { User as UserIcon, Phone, Mail, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile } from '../lib/users';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  FormField,
  Input,
  PageHeader,
  Row,
  Spinner,
  Stack,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';

export default function SettingsProfilePage() {
  const { user, profile, loading } = useAuth();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [seeded, setSeeded] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Seed once when the profile first arrives. Subsequent snapshot updates
  // (e.g. from another tab) shouldn't overwrite mid-edit; we resync only
  // after a successful save (which itself triggers a snapshot).
  useEffect(() => {
    if (loading || !profile || seeded) return;
    setName(profile.name ?? '');
    setPhone(profile.phone ?? '');
    setSeeded(true);
  }, [profile, loading, seeded]);

  if (loading || !profile || !user) {
    return (
      <AppShell>
        <Container>
          <Row gap={2} align="center">
            <Spinner /> <span className="ds-text-muted">Loading…</span>
          </Row>
        </Container>
      </AppShell>
    );
  }

  const dirty = name.trim() !== (profile.name ?? '') || phone.trim() !== (profile.phone ?? '');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !dirty) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const trimmedPhone = phone.trim();
      await updateUserProfile({
        name: name.trim(),
        // Empty string clears phone; otherwise pass through.
        phone: trimmedPhone === '' ? null : trimmedPhone,
      });
      setInfo('Saved.');
      // The AuthContext snapshot will update profile in a tick; no need
      // to setName/setPhone here.
    } catch (err) {
      console.error('[profile] update failed', err);
      setError(extractMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell>
      <Container>
        <PageHeader title="Profile" subtitle="Your account details and contact information." />

        <Stack gap={6}>
          {error && <ErrorBanner message={error} />}
          {info && (
            <div
              style={{
                background: 'var(--color-success-soft)',
                border: '1px solid rgba(34, 197, 94, 0.2)',
                color: 'var(--color-success-text)',
                padding: 'var(--space-3) var(--space-4)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--fs-sm)',
              }}
            >
              {info}
            </div>
          )}

          <Card>
            <CardHeader>
              <Row gap={2} align="center">
                <UserIcon size={16} strokeWidth={1.75} color="var(--color-accent)" />
                <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>
                  Identity
                </h2>
              </Row>
            </CardHeader>
            <CardBody>
              <Stack gap={4} as="form" onSubmit={submit}>
                <FormField label="Full name">
                  {(id) => (
                    <Input
                      id={id}
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      autoComplete="name"
                      maxLength={80}
                      placeholder="Marc Jolly"
                    />
                  )}
                </FormField>

                <FormField
                  label="Phone"
                  hint="International format, e.g. +447700900123. Used for SMS notifications and as a fallback second factor."
                >
                  {(id) => (
                    <Input
                      id={id}
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      autoComplete="tel"
                      placeholder="+447700900123"
                    />
                  )}
                </FormField>

                <Row justify="end">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!dirty}
                    loading={busy}
                    iconLeft={!busy ? <Save size={14} strokeWidth={2} /> : undefined}
                  >
                    Save changes
                  </Button>
                </Row>
              </Stack>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Row gap={2} align="center">
                <Mail size={16} strokeWidth={1.75} color="var(--color-accent)" />
                <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>
                  Account
                </h2>
              </Row>
            </CardHeader>
            <CardBody>
              <Stack gap={4}>
                <Row gap={4} align="center" justify="between">
                  <Stack gap={1}>
                    <span className="ds-label">Email</span>
                    <span className="ds-mono">{profile.email}</span>
                  </Stack>
                  {user.emailVerified ? (
                    <Badge tone="success" dot>
                      Verified
                    </Badge>
                  ) : (
                    <Badge tone="warning" dot>
                      Unverified
                    </Badge>
                  )}
                </Row>

                <Row gap={4} align="center" justify="between">
                  <Stack gap={1}>
                    <span className="ds-label">Two-factor auth</span>
                    <span className="ds-text-sm ds-text-muted">
                      Required at signup. Manage from your authenticator app or phone.
                    </span>
                  </Stack>
                  {profile.mfaEnrolled ? (
                    <Badge tone="success" dot>
                      Enabled
                    </Badge>
                  ) : (
                    <Badge tone="danger" dot>
                      Missing
                    </Badge>
                  )}
                </Row>
              </Stack>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Row gap={2} align="center">
                <Phone size={16} strokeWidth={1.75} color="var(--color-accent)" />
                <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>
                  Notifications
                </h2>
                <Badge tone="neutral">Coming soon</Badge>
              </Row>
            </CardHeader>
            <CardBody>
              <p className="ds-text-muted ds-text-sm">
                Channel priority, quiet hours, and per-service overrides land in a later chunk —
                close to when the incident dispatcher needs them.
              </p>
            </CardBody>
          </Card>
        </Stack>
      </Container>
    </AppShell>
  );
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}
